import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Plus, TicketPercent } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import {
  DataTable,
  useDataTable,
  useSearchListState,
  type SearchPatch,
} from '@nucleus-commerce/admin-kit/index-table';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import { Button } from '@nucleus-commerce/ui/components/button';

import { useStore } from '@/shared/store/store-context';

import { useDiscountColumns } from '../components/discount-columns';
import { discountFilters } from '../components/discount-filters';
import { useDiscounts } from '../hooks/use-discounts';

const ROUTE_ID = '/_authed/_active/s/$storeId/discounts/' as const;
const ROUTE_PATH = '/s/$storeId/discounts/' as const;

function Discounts() {
  const { t } = useTranslation('discounts');
  const store = useStore();
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: ROUTE_PATH });

  const filters = useMemo(() => discountFilters(t), [t]);
  const onSearchChange = useCallback(
    (patch: SearchPatch) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const list = useSearchListState({ search, onSearchChange, filters });
  const query = useDiscounts(store.id, list.params);
  const columns = useDiscountColumns(store.id);
  const table = useDataTable({
    mode: 'server',
    data: query.data?.items ?? [],
    nextCursor: query.data?.nextCursor,
    list,
    columns,
    filters,
    getRowId: (row) => row.id,
    tableId: 'discounts',
  });

  const narrowed = Boolean(list.q) || Object.keys(list.filters).length > 0 || Boolean(list.cursor);
  const isEmptyList = query.isSuccess && query.data.items.length === 0 && !narrowed;

  const addButton = (tour?: boolean) => (
    <Can permission="discounts.write">
      <Button asChild data-tour={tour ? 'discounts.list.create' : undefined}>
        <Link to="/s/$storeId/discounts/new" params={{ storeId: store.id }}>
          <Plus aria-hidden />
          {t('create.action')}
        </Link>
      </Button>
    </Can>
  );

  return (
    <PageContent>
      <PageHeader
        title={t('title')}
        description={t('description', { store: store.name })}
        actions={addButton(true)}
      />
      {isEmptyList ? (
        <EmptyState
          icon={TicketPercent}
          title={t('empty.title')}
          description={t('empty.description')}
          tour="discounts.list.empty"
          tourLink
          action={addButton()}
        />
      ) : (
        <DataTable
          table={table}
          search={{ placeholder: t('search'), label: t('search') }}
          loading={query.isPending}
          fetching={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          onRowClick={(row) =>
            void navigate({
              to: '/s/$storeId/discounts/$discountId',
              params: { storeId: store.id, discountId: row.id },
            })
          }
          getRowLabel={(row) => row.title}
          resourceName={{ singular: t('resource.one'), plural: t('resource.other') }}
          emptyState={{ title: t('noMatches') }}
          tour={{ table: 'discounts.list.table', search: 'discounts.list.search' }}
        />
      )}
    </PageContent>
  );
}

export function DiscountsPage() {
  return (
    <FeatureErrorBoundary name="discounts.list">
      <Discounts />
    </FeatureErrorBoundary>
  );
}
